import React, { useState, useEffect } from "react";
import { supabase } from "../../services/supabase";
import { fetchAllRows } from "../../utils/fetchAllRows";
import type { OrganizationAssociation } from "../../types/voucherTemplate.types";
import { preventEnterSubmit } from "../../utils/preventEnterSubmit";

interface Organization {
  id: string;
  name: string;
}

interface Location {
  id: string;
  name: string;
  organization_id: string;
}

interface Props {
  selected: OrganizationAssociation[];
  onChange: (selected: OrganizationAssociation[]) => void;
}

const OrganizationLocationSelector: React.FC<Props> = ({
  selected,
  onChange,
}) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load organizations and locations
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Page through ALL orgs and ALL locations — a single .limit() cannot
        // exceed PostgREST's 1000-row cap, so these pickers would silently
        // truncate (prod has ~2,658 orgs). See fetchAllRows.
        const orgs = await fetchAllRows<Organization>((from, to) =>
          supabase
            .from("organizations")
            .select("id, name")
            .eq("is_archived", false)
            .order("name")
            .range(from, to),
        );

        const locs = await fetchAllRows<Location>((from, to) =>
          supabase
            .from("locations")
            .select("id, name, organization_id")
            .eq("is_archived", false)
            .order("name")
            .range(from, to),
        );

        setOrganizations(orgs);
        setLocations(locs);
      } catch (error) {
        console.error("Error loading organizations/locations:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleAddOrganization = (org: Organization) => {
    const exists = selected.some(
      (s) => s.org_id === org.id && s.location_id === null,
    );
    if (!exists) {
      onChange([
        ...selected,
        {
          org_id: org.id,
          org_name: org.name,
          location_id: null,
        },
      ]);
    }
    setSearchQuery("");
    setShowDropdown(false);
  };

  const handleAddLocation = (loc: Location, org: Organization) => {
    const exists = selected.some((s) => s.location_id === loc.id);
    if (!exists) {
      onChange([
        ...selected,
        {
          org_id: org.id,
          org_name: org.name,
          location_id: loc.id,
          location_name: loc.name,
        },
      ]);
    }
    setSearchQuery("");
    setShowDropdown(false);
  };

  const handleRemove = (index: number) => {
    onChange(selected.filter((_, i) => i !== index));
  };

  // Filter options based on search query
  const filteredOrganizations = organizations.filter((org) =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredLocations = locations.filter((loc) => {
    const org = organizations.find((o) => o.id === loc.organization_id);
    const orgName = org?.name || "";
    const combined = `${orgName} - ${loc.name}`.toLowerCase();
    return combined.includes(searchQuery.toLowerCase());
  });

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Organizations
        <span className="text-gray-500 text-xs ml-2">
          (Search and select organizations or specific locations)
        </span>
      </label>

      {/* Selected Items */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selected.map((item, index) => (
            <div
              key={index}
              className="inline-flex items-center gap-2 bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm"
            >
              <span>
                {item.org_name}
                {item.location_name && ` - ${item.location_name}`}
              </span>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="hover:text-primary-900"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <input
          onKeyDown={preventEnterSubmit}
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder="Search organizations or locations..."
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />

        {/* Dropdown */}
        {showDropdown && (searchQuery || filteredOrganizations.length > 0) && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : (
              <>
                {/* Organizations */}
                {filteredOrganizations.length > 0 && (
                  <div>
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50">
                      Organizations
                    </div>
                    {filteredOrganizations.slice(0, 5).map((org) => (
                      <button
                        key={org.id}
                        type="button"
                        onClick={() => handleAddOrganization(org)}
                        className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                        <span>{org.name}</span>
                        <span className="text-xs text-gray-500 ml-auto">
                          (All locations)
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Locations */}
                {filteredLocations.length > 0 && (
                  <div>
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50">
                      Specific Locations
                    </div>
                    {filteredLocations.slice(0, 10).map((loc) => {
                      const org = organizations.find(
                        (o) => o.id === loc.organization_id,
                      );
                      if (!org) {
                        return null;
                      }
                      return (
                        <button
                          key={loc.id}
                          type="button"
                          onClick={() => handleAddLocation(loc, org)}
                          className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <svg
                            className="w-4 h-4 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          <span className="text-sm">
                            <span className="font-medium">{org.name}</span>
                            <span className="text-gray-500"> - {loc.name}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {filteredOrganizations.length === 0 &&
                  filteredLocations.length === 0 && (
                    <div className="p-4 text-center text-gray-500">
                      No results found
                    </div>
                  )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Click outside to close */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowDropdown(false)}
        ></div>
      )}
    </div>
  );
};

export default OrganizationLocationSelector;
